import {
  Event,
  EventType,
  PaymentMethod,
  type BuySubmissionFormEvent,
  type SecureQuoteFormSubmissionEvent,
  type SellSubmissionFormEvent,
  type WebflowFormSubmissionEvent,
} from "@/core/entities/event";
import { ProductType, type Product } from "@/core/entities/product";
import {
  Calendar,
  FileText,
  Shield,
  ShoppingCart,
  TrendingUp,
} from "lucide-react";

// Event Type Utilities for EventsDialog
export const getEventTypeIcon = (type: EventType) => {
  switch (type) {
    case EventType.WEBFLOW_FORM_SUBMISSION:
      return <FileText className="h-4 w-4" />;
    case EventType.SECURE_QUOTE:
      return <Shield className="h-4 w-4" />;
    case EventType.SELL_SUBMISSION_FORM:
      return <ShoppingCart className="h-4 w-4" />;
    case EventType.BUY_SUBMISSION_FORM:
      return <ShoppingCart className="h-4 w-4" />;
    default:
      return <Calendar className="h-4 w-4" />;
  }
};

export const getEventTypeBadgeVariant = (type: EventType) => {
  switch (type) {
    case EventType.WEBFLOW_FORM_SUBMISSION:
      return "outline";
    case EventType.SECURE_QUOTE:
      return "outline";
    case EventType.SELL_SUBMISSION_FORM:
      return "secondary";
    case EventType.BUY_SUBMISSION_FORM:
      return "default";
    default:
      return "outline";
  }
};

export const formatEventType = (type: EventType) => {
  switch (type) {
    case EventType.WEBFLOW_FORM_SUBMISSION:
      return "Webflow Form";
    case EventType.SECURE_QUOTE:
      return "Secure Quote";
    case EventType.SELL_SUBMISSION_FORM:
      return "Sell Submission";
    case EventType.BUY_SUBMISSION_FORM:
      return "Buy Submission";
    default:
      return type;
  }
};

// Data Extraction Utilities
export const getProductName = (
  productId: string,
  products: Product[] | null,
) => {
  if (!products) return productId;
  const product = products.find((p) => p.id === productId);
  return product?.name ?? productId;
};

export const getProduct = (productId: string, products: Product[] | null) => {
  if (!products) return null;
  return products.find((p) => p.id === productId) ?? null;
};

export const getOrderType = (event: Event) => {
  switch (event.type) {
    case EventType.WEBFLOW_FORM_SUBMISSION:
      return (event as WebflowFormSubmissionEvent).data?.buyOrSell ?? null;
    case EventType.SECURE_QUOTE:
      return (event as SecureQuoteFormSubmissionEvent).data?.type ?? null;
    case EventType.SELL_SUBMISSION_FORM:
      return ProductType.SELL;
    case EventType.BUY_SUBMISSION_FORM:
      return ProductType.BUY;
    default:
      return null;
  }
};

export const getMiles = (event: Event) => {
  if (event.type === EventType.WEBFLOW_FORM_SUBMISSION) {
    return (event as WebflowFormSubmissionEvent).data?.amount ?? null;
  }
  if (event.type === EventType.SECURE_QUOTE) {
    return (event as SecureQuoteFormSubmissionEvent).data?.miles ?? null;
  }
  if (event.type === EventType.SELL_SUBMISSION_FORM) {
    return (event as SellSubmissionFormEvent).data?.miles ?? null;
  }
  if (event.type === EventType.BUY_SUBMISSION_FORM) {
    return (event as BuySubmissionFormEvent).data?.miles ?? null;
  }
  return null;
};

export const getRate = (event: Event) => {
  if (event.type === EventType.SECURE_QUOTE) {
    return (event as SecureQuoteFormSubmissionEvent).data?.averageRate ?? null;
  }
  if (event.type === EventType.SELL_SUBMISSION_FORM) {
    return (event as SellSubmissionFormEvent).data?.averageRate ?? null;
  }
  if (event.type === EventType.BUY_SUBMISSION_FORM) {
    return (event as BuySubmissionFormEvent).data?.averageRate ?? null;
  }
  return null;
};

export const getTotalPayout = (event: Event) => {
  if (event.type === EventType.SECURE_QUOTE) {
    return (event as SecureQuoteFormSubmissionEvent).data?.totalPayout ?? null;
  }
  if (event.type === EventType.SELL_SUBMISSION_FORM) {
    return (event as SellSubmissionFormEvent).data?.totalPayout ?? null;
  }
  if (event.type === EventType.BUY_SUBMISSION_FORM) {
    return (event as BuySubmissionFormEvent).data?.totalPayout ?? null;
  }
  return null;
};

export const getPaymentMethod = (event: Event) => {
  if (event.type === EventType.SELL_SUBMISSION_FORM) {
    return (event as SellSubmissionFormEvent).data?.paymentMethod ?? null;
  }
  return null;
};

// Formatting Utilities
export const formatCurrency = (amount: number | null) => {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

export const formatMiles = (miles: number | null) => {
  if (miles === null || miles === undefined) return "-";
  return new Intl.NumberFormat("en-US").format(miles);
};

export const formatRate = (rate: number | null) => {
  if (rate === null || rate === undefined) return "-";
  return `$${rate}`;
};

export const formatPaymentMethod = (method: PaymentMethod | null) => {
  if (!method) return "-";
  switch (method) {
    case PaymentMethod.PAYPAL:
      return "PayPal";
    case PaymentMethod.ECHECK:
      return "Echeck";
    case PaymentMethod.WIRE:
      return "Wire";
    default:
      return method;
  }
};

// Badge and Icon Utilities
export const getOrderTypeIcon = (orderType: ProductType | null) => {
  switch (orderType) {
    case ProductType.BUY:
      return <TrendingUp className="h-4 w-4 text-green-600" />;
    case ProductType.SELL:
      return <TrendingUp className="h-4 w-4 text-green-600" />;
    default:
      return null;
  }
};

export const getOrderTypeBadgeVariant = (orderType: ProductType | null) => {
  switch (orderType) {
    case ProductType.BUY:
      return "default";
    case ProductType.SELL:
      return "default";
    default:
      return "outline";
  }
};
